import {faFile, faMessageBot, faRepeat} from '@fortawesome/pro-regular-svg-icons';
import {faSpinner} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Space, Spin, Tooltip} from 'antd';
import {TextAreaRef} from 'antd/lib/input/TextArea';
import {camelizeKeys} from 'humps';
import {
    Dispatch,
    ReactElement,
    ReactNode,
    RefObject,
    SetStateAction,
    SyntheticEvent,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';

import {gray1} from '../../../../../../../../css/var-export.scss';
import {useLicenses} from '../../../../../../../../provider/license/license-hook';
import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {useSnackbar} from '../../../../../../../../provider/snackbar/snackbar-hook';
import {useSystem} from '../../../../../../../../provider/system/system-hook';
import {useUser} from '../../../../../../../../provider/user/user-hook';
import {AnalyticsTarget, track} from '../../../../../../../../service/analytics/analytics';
import {useReviewRealtimePresence} from '../../../../../../../../service/reviews/review-realtime-presence/review-realtime-presence-context';
import {ReviewPresenceActionEnum} from '../../../../../../../../service/reviews/review-realtime-presence/review-realtime-presence-type';
import {fetchReviewsReply} from '../../../../../../../../service/reviews/reviews-reply';
import {
    ResponseTemplateDataType,
    useAllResponseTemplateList,
} from '../../../../../../../../service/reviews/reviews-response-templates';
import {ReviewDataBrandFromListType, ReviewDataChildType} from '../../../../../../../../service/reviews/reviews-type';
import {FeaturesEnum} from '../../../../../../../../service/user/user-type';
import {classNames} from '../../../../../../../../util/css';
import {getFromLocalStorage, saveToLocalStorage} from '../../../../../../../../util/local-storage';
import {ReviewReplyTextAreaWithEmoji} from '../../../../../review-reply-emoji-picker/review-reply-textarea-with-emoji';
import {MIN_ROWS_TEXTAREA} from '../../../../../reviews-const';
import {useReviewStateHook} from '../../../../../reviews-state/reviews-state-provider';
import {handleReviewReplyExceptions, isOnlyEmojis, newHandleEnter} from '../../review-helper';
import * as styles from '../new-reply-form.scss';
import {ReplyTypeEnum} from '../reply-form-const';
import {GetNeuralLinkButtonWrapper} from '../trial-popover/trial-popover';

import {ReplySourceTypeEnum, TEST_AND_DEMO_USER_COUNT_AI_STORAGE_KEY} from './ai-review-generator-button-const';
import {localStorageAiButtonShowSchema, LocalStorageAiButtonShowType} from './ai-review-generator-button-type';

type PropsType = {
    reviewId: number;
    replyAreaRef: RefObject<TextAreaRef>;
    brand: ReviewDataBrandFromListType;
    setIsSelectTemplatePopupOpen: Dispatch<SetStateAction<boolean>>;
    answerType: string;
    setAnswerType: Dispatch<SetStateAction<string>>;
    isShowGenerateButton: boolean;
    answerText: string;
    setAnswerText: (value: string) => void;
    comment: string | null;
    extraActions?: ReactNode;
};

// eslint-disable-next-line complexity,max-statements
export function NewAiReviewGeneratorButton(props: PropsType): ReactElement {
    const {
        reviewId,
        replyAreaRef,
        brand,
        setIsSelectTemplatePopupOpen,
        setAnswerType,
        answerType,
        isShowGenerateButton,
        setAnswerText,
        answerText,
        comment,
        extraActions,
    } = props;

    const systemContext = useSystem();
    const {getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const {licenses, refetch} = useLicenses();
    const {user} = useUser();

    const reviewGptReplyLicense = licenses[FeaturesEnum.reviewGptReplySuggestions];
    const internalAgencyReviewLicense = licenses[FeaturesEnum.bulkGenerateButton]?.isActive;
    const [isReviewReplyInProgress, setIsReviewReplyInProgress] = useState<boolean>(false);
    const [wasGeneration, setWasGeneration] = useState<boolean>(false);
    const [isQuickTemplateMenuOpen, setIsQuickTemplateMenuOpen] = useState<boolean>(false);

    const {refreshReviewWithAnswer, refreshSuggestions, requestAIGenerate, isReplyGenerating} = useReviewStateHook();
    const {stopActivity} = useReviewRealtimePresence();
    const [hasInteractedWithTextArea, setHasInteractedWithTextArea] = useState<boolean>(false);
    // The reply field is now open by default for every unanswered review, so fetching the quick-template
    // list only once the user actually focuses it avoids firing one request per visible review on page load.
    const {data: templateList, isFetching: isTemplatesLoading} = useAllResponseTemplateList(
        reviewId,
        // eslint-disable-next-line no-undefined
        undefined,
        hasInteractedWithTextArea || answerText.length > 0
    );

    const localStorageShowButton = getFromLocalStorage<LocalStorageAiButtonShowType>(
        TEST_AND_DEMO_USER_COUNT_AI_STORAGE_KEY,
        localStorageAiButtonShowSchema,
        {}
    );
    const isButtonLoading = isReplyGenerating(reviewId);
    const isButtonDisabled = answerText?.length === 0 || brand.isTest;

    const isAiButtonDisabled =
        (user?.isDemoCpUser || user?.isTestUser) && (localStorageShowButton?.[reviewId]?.count ?? 0) > 1;

    const handleSendReply = useCallback(async () => {
        if (isButtonDisabled || isReviewReplyInProgress) {
            return;
        }

        try {
            setIsReviewReplyInProgress(true);
            const reviewReply = await fetchReviewsReply(reviewId, {
                comment: answerText.trim(),
                ...(wasGeneration ? {replySourceType: ReplySourceTypeEnum.Neuronetwork} : {}),
            });

            await refreshReviewWithAnswer(camelizeKeys(reviewReply) as ReviewDataChildType, reviewId);

            await refreshSuggestions();

            snackbar.success({
                message: <Locale stringKey="REVIEWS__REVIEW_ANSWER_SEND_SUCCESS__TITLE" />,
                description: <Locale stringKey="REVIEWS__REVIEW_ANSWER_SEND_SUCCESS__DESCRIPTION" />,
            });

            track(AnalyticsTarget.ReviewsManagement.Reviews.ReplyToReview, answerType);

            stopActivity({
                reviewId,
                brandId: brand.id,
                action: ReviewPresenceActionEnum.Reply,
            });

            setAnswerText('');
        } catch (error: unknown) {
            handleReviewReplyExceptions(error, snackbar);
        } finally {
            setIsReviewReplyInProgress(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isButtonDisabled, isReviewReplyInProgress, reviewId, answerText, refreshSuggestions, snackbar, answerType]);

    function handlePressEnter(event: React.KeyboardEvent<HTMLTextAreaElement>): void {
        newHandleEnter(event, systemContext.screen.isDesktop ? handleSendReply : null);
    }

    function handleSelectTemplateClick() {
        track(AnalyticsTarget.ReviewsManagement.Reviews.ClickResponseTemplates);

        setIsSelectTemplatePopupOpen(true);
    }

    const slashTimerRef = useRef<NodeJS.Timeout | null>(null);

    function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>): void {
        if (slashTimerRef.current) {
            clearTimeout(slashTimerRef.current);
        }

        if (event.key === '/') {
            slashTimerRef.current = setTimeout(() => {
                setIsQuickTemplateMenuOpen(true);
                slashTimerRef.current = null;
            }, 200);
        }

        if (event.key === 'Escape' && isQuickTemplateMenuOpen) {
            setIsQuickTemplateMenuOpen(false);
        }
    }

    function handleQuickTemplateSelect(template: ResponseTemplateDataType) {
        setAnswerText(template.message);
        setAnswerType(ReplyTypeEnum.TemplateReply);
        setIsQuickTemplateMenuOpen(false);
        track(AnalyticsTarget.ReviewsManagement.Reviews.AddResponseTemplate, template.id);
    }

    function handleQuickTemplateMenuClose(): void {
        setIsQuickTemplateMenuOpen(false);
    }

    function handleQuickTemplateMenuOpenChange(isOpen: boolean): void {
        setIsQuickTemplateMenuOpen(isOpen);
    }

    function handleRequestAi() {
        if (isButtonLoading) {
            return;
        }

        setAnswerText('');
        requestAIGenerate(reviewId);
        setWasGeneration(true);

        if (user?.isTestUser || user?.isDemoCpUser) {
            const currentCount = localStorageShowButton?.[reviewId]?.count ?? 0;

            saveToLocalStorage(
                {
                    ...localStorageShowButton,
                    [reviewId]: {
                        count: currentCount + 1,
                    },
                },
                TEST_AND_DEMO_USER_COUNT_AI_STORAGE_KEY
            );
        }
    }

    const aiButtonClassNames = classNames(styles.ReplyForm_neuralLinkButton, {
        [styles.ReplyForm_neuralLinkButton__isGenerating]: isButtonLoading,
    });

    function handleAnswerTextChange(nextValue: string): void {
        setAnswerText(nextValue);

        if (isQuickTemplateMenuOpen) {
            setIsQuickTemplateMenuOpen(false);
        }

        setAnswerType((type) => {
            if (type.includes(ReplyTypeEnum.TemplateReply)) {
                return `${ReplyTypeEnum.TemplateReply}, ${ReplyTypeEnum.ManualReply}`;
            }

            return ReplyTypeEnum.ManualReply;
        });
    }

    const isEmojiCommentReview = useMemo(() => isOnlyEmojis(comment), [comment]);

    return (
        <Space className={styles.ReplyForm_replyFormWrapper} direction="vertical">
            <div className={styles.ReplyForm_textAreaBlock}>
                <ReviewReplyTextAreaWithEmoji
                    autoSize
                    onFocus={() => setHasInteractedWithTextArea(true)}
                    onInput={(event: SyntheticEvent<HTMLTextAreaElement>) => {
                        handleAnswerTextChange(event.currentTarget.value);
                    }}
                    onKeyDown={handleKeyDown}
                    onPressEnter={handlePressEnter}
                    onValueChange={handleAnswerTextChange}
                    placeholder={getLocalizedString('REVIEWS__ENTER_YOUR_REPLY_TEXT')}
                    quickTemplatePopover={{
                        isLoading: isTemplatesLoading,
                        isOpen: isQuickTemplateMenuOpen,
                        onClickOutside: handleQuickTemplateMenuClose,
                        onOpenChange: handleQuickTemplateMenuOpenChange,
                        onTemplateSelect: handleQuickTemplateSelect,
                        templates: templateList || [],
                    }}
                    rows={MIN_ROWS_TEXTAREA}
                    size="large"
                    textareaClassName={styles.ReplyForm_textArea}
                    textareaRef={replyAreaRef}
                    value={answerText}
                />
                <div className={styles.ReplyForm_buttonsWrapper}>
                    {isShowGenerateButton && (
                        <GetNeuralLinkButtonWrapper
                            isCanActivateTrial={reviewGptReplyLicense?.canActivateTrial}
                            isOnlyEmojiComment={isEmojiCommentReview}
                            isTrialWrapper={internalAgencyReviewLicense ? false : !reviewGptReplyLicense?.isActive}
                            refetch={refetch}
                        >
                            <Button
                                aria-label={getLocalizedString(
                                    isButtonLoading
                                        ? 'NEURAL_NETWORK__GENERATE_BUTTON__GENERATE'
                                        : wasGeneration
                                        ? 'NEURAL_NETWORK__GENERATE_BUTTON__GENERATE_MORE'
                                        : 'NEURAL_NETWORK__GENERATE_BUTTON'
                                )}
                                className={aiButtonClassNames}
                                disabled={
                                    internalAgencyReviewLicense
                                        ? false
                                        : !reviewGptReplyLicense?.isActive || isAiButtonDisabled || isEmojiCommentReview
                                }
                                id="GU30W"
                                onClick={handleRequestAi}
                                type="primary"
                            >
                                {isButtonLoading ? (
                                    <Spin indicator={<FontAwesomeIcon color={gray1} icon={faSpinner} spin />} />
                                ) : wasGeneration ? (
                                    <FontAwesomeIcon height={32} icon={faRepeat} width={32} />
                                ) : (
                                    <FontAwesomeIcon height={32} icon={faMessageBot} width={32} />
                                )}
                            </Button>
                        </GetNeuralLinkButtonWrapper>
                    )}
                    <Tooltip placement="top" title={<Locale stringKey="REVIEWS__REVIEW_ANSWER__TEMPLATE__HINT" />}>
                        <Button className={styles.ReplyForm_templateButton} onClick={handleSelectTemplateClick}>
                            <FontAwesomeIcon height={32} icon={faFile} width={32} />
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <div className={styles.ReplyForm_sendButtonsWrapper}>
                <Space>
                    {answerText.length > 0 && (
                        <>
                            <Button
                                disabled={isButtonDisabled}
                                loading={isReviewReplyInProgress}
                                onClick={handleSendReply}
                                type="primary"
                            >
                                <Locale stringKey="REVIEWS__REPLY" />
                            </Button>

                            {/* The reply field can no longer be collapsed, so Cancel just discards the draft. */}
                            <Button onClick={() => setAnswerText('')}>
                                <Locale stringKey="BUTTON__CANCEL" />
                            </Button>
                        </>
                    )}
                </Space>
                {extraActions}
            </div>
        </Space>
    );
}
